import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { fetchWithAuth } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AssetModal = ({ asset, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: asset?.name || '', type: asset?.type || 'Trust',
    country: asset?.country || '', notes: asset?.notes || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const assetTypes = ['Trust', 'Foundation'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const url = asset ? `${BACKEND_URL}/api/related-assets/${asset.asset_id}` : `${BACKEND_URL}/api/related-assets`;
      const response = await fetchWithAuth(url, {
        method: asset ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || 'Failed to save asset'); }
      toast.success(asset ? 'Asset updated successfully' : 'Asset created successfully');
      onSuccess();
    } catch (error) { toast.error(error.message); }
    finally { setIsSubmitting(false); }
  };

  const fields = [
    { label: 'Asset Name *', type: 'text', key: 'name', placeholder: 'e.g., Family Trust 2025', required: true },
    { label: 'Asset Type *', type: 'select', key: 'type', options: assetTypes, required: true },
    { label: 'Country of Jurisdiction *', type: 'text', key: 'country', placeholder: 'e.g., Cayman Islands', required: true },
    { label: 'Notes (Optional)', type: 'textarea', key: 'notes', placeholder: 'Additional notes about this asset' },
  ];

  const renderField = (f) => {
    const baseClass = "w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-indigo-500";
    if (f.type === 'select') {
      return (
        <select required={f.required} value={formData[f.key]} onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })} className={baseClass}>
          {f.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === 'textarea') {
      return <textarea value={formData[f.key]} onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })} rows={3} className={baseClass} placeholder={f.placeholder} />;
    }
    return <input type="text" required={f.required} value={formData[f.key]} onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })} className={baseClass} placeholder={f.placeholder} />;
  };

  const getSubmitLabel = () => {
    if (isSubmitting) return 'Saving...';
    return asset ? 'Update' : 'Create';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-2xl font-bold text-slate-900 mb-4">{asset ? 'Edit Related Asset' : 'Add New Related Asset'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map(f => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                {renderField(f)}
              </div>
            ))}
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                {getSubmitLabel()}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssetModal;
